import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { Catalog, ProcessDoc, ProcessPatch, ProcessSummary } from '@wand/shared';
import { api } from './client.js';
import { orgKey } from './keys.js';
import { useOrgId } from '../org/OrgContext.js';
import { useGateway } from '../gateway/GatewayContext.js';

/** Catalog is static reference data served by the BFF (org-agnostic). */
export const qk = {
  catalog: ['catalog'] as const,
};

export function useCatalog(): UseQueryResult<Catalog> {
  return useQuery({
    queryKey: qk.catalog,
    queryFn: api.catalog,
    staleTime: Infinity,
  });
}

/* ---- process documents (the canvas) — routed through the gateway ---- */

export function useProcessList(): UseQueryResult<ProcessSummary[]> {
  const org = useOrgId();
  const gw = useGateway();
  return useQuery({ queryKey: orgKey.processDocs(org), queryFn: () => gw.listProcessDocs(org) });
}

export function useProcess(id: string | undefined): UseQueryResult<ProcessDoc | null> {
  const org = useOrgId();
  const gw = useGateway();
  return useQuery({
    queryKey: orgKey.processDoc(org, id ?? 'none'),
    // `null`, not `undefined`: "no document at this id" is a legitimate answer that
    // `CanvasRoute` acts on, but TanStack Query treats an `undefined` result as a
    // broken query function and logs an error. The gateway keeps returning
    // `undefined` — its contract suite pins that — so the coercion lives here.
    queryFn: async () => (await gw.getProcessDoc(org, id!)) ?? null,
    enabled: !!id,
  });
}

/** Persist process edits. Optimistically updates the cached doc. */
export function useSaveProcess(id: string | undefined) {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: ProcessPatch) => gw.saveProcessDoc(org, id!, patch),
    onSuccess: (doc) => {
      qc.setQueryData(orgKey.processDoc(org, doc.id), doc);
      void qc.invalidateQueries({ queryKey: orgKey.processDocs(org) });
    },
  });
}

/** Create a new process doc, then invalidate the org's list. */
export function useCreateProcess() {
  const org = useOrgId();
  const gw = useGateway();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name?: string) => gw.createProcessDoc(org, name),
    onSuccess: () => void qc.invalidateQueries({ queryKey: orgKey.processDocs(org) }),
  });
}

export function useWizard(processId: string | undefined) {
  return useMutation({
    mutationFn: (message: string) => api.wizard(message, processId),
  });
}
